import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import AddFilesIcon from "../../assets/images/policies-manuals/addfiles-icon.svg";
import DocumentViewer from "../../components/DocumentViewer/DocumentViewer";
import { XIcon, PlusIcon, ArrowRight, Trash2 } from "lucide-react";
import images from "../../assets/images";
import AddFileModal, {
  AssignedToUser,
  PolicyUploadPayload,
} from "./AddFileModal";
import {
  getFiles,
  updatefilefolder,
  searchPolicies,
  getFolderTags,
  addTags,
  getAllPolicyTags,
  getUserDirectory,
} from "../../service/api";
import Modal from "../../components/modal/Modal";
import ConfirmModal from "../../components/modal/ConfirmModal";
import { SpinLoader, DotLoader } from "../../components/loader/Loader";
import { useTaskStore, usePolicyTagStore } from "../../store/store";
import { datesubstract, isDefined, sanitizeName } from "../../common/utils";
import { Dropdown } from "../../components/dropdown/Dropdown";
import { MultiSelectDropdown } from "../../components/dropdown/MultiSelectDropdown";
import { usePermissions } from "../../hooks/usePermission";
import { useS3Upload, getS3SignedUrl } from "../../utils/s3Client";
import { useAuth } from "../../auth/AuthProvider";
import { PoliciesModuleConfig, fireSafetyConfig } from "./policiesConfig";

interface PolicyFile {
  name: string;
  originalName?: string;
  size: string;
  tags: string[];
  expiry: string;
  reminder: string;
  url: string;
  type: string;
  region?: string;
  premises?: string;
  // "standard" variant only (Fire Safety / Hazardous Materials)
  assignedTo?: { name: string; email: string };
  // "certificate" variant only (Laws & Regulations)
  certificateName?: string;
  issueDate?: string;
  issuedBy?: string;
  isLifetime?: boolean | string | number;
}

interface FolderViewProps {
  config?: PoliciesModuleConfig;
}

const formatDateForInput = (dateStr: string): string => {
  if (!dateStr || typeof dateStr !== "string") return "";
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

const normalizeIsLifetime = (value: any): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const trimmed = value.trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(trimmed)) return true;
    if (["false", "0", "no", "n", ""].includes(trimmed)) return false;
  }
  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  if (value === null || value === undefined) return false;
  return Boolean(value);
};

const isEmptyDateField = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  return false;
};

const FolderView = ({ config = fireSafetyConfig }: FolderViewProps) => {
  const isCertificate = config.variant === "certificate";
  const { permissionModule, labels } = config;

  const { tasks, loading, addTask } = useTaskStore();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id } = useParams();
  const location = useLocation();
  const navState = (location.state || {}) as {
    selectedRegion?: string;
    selectedPremises?: string;
  };
  const [selectedRegion, setSelectedRegion] = useState<string>(
    navState.selectedRegion || "ALL Regions",
  );
  const [selectedPremises, setSelectedPremises] = useState<string>(
    navState.selectedPremises || "ALL Premises",
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editFileModalOpen, setEditFileModalOpen] = useState(false);
  const [resMessage, setResMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [editFile, setEditFile] = useState<PolicyFile | null>(null);
  const [fileToDelete, setFileToDelete] = useState<PolicyFile | null>(null);
  const [editFileName, setEditFileName] = useState<string>("");
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editExpiry, setEditExpiry] = useState<string>("");
  const [editReminder, setEditReminder] = useState<string>("");
  const [editRegion, setEditRegion] = useState<string>("");
  const [editPremises, setEditPremises] = useState<string>("");
  // Standard variant only: file assignee
  const [editAssignedTo, setEditAssignedTo] = useState<string>("");
  const [editAvailableUsers, setEditAvailableUsers] = useState<
    AssignedToUser[]
  >([]);
  const [editLoadingUsers, setEditLoadingUsers] = useState<boolean>(false);
  // Certificate variant only
  const [editCertificateName, setEditCertificateName] = useState<string>("");
  const [editIssueDate, setEditIssueDate] = useState<string>("");
  const [editIssuedBy, setEditIssuedBy] = useState<string>("");
  const [editIsLifetime, setEditIsLifetime] = useState<boolean>(false);

  const [editAvailableTags, setEditAvailableTags] = useState<
    { label: string; value: string }[]
  >([]);
  const [newTag, setNewTag] = useState<{ label: string; value: string }>({
    label: "",
    value: "",
  });
  const [isNewTag, setIsNewTag] = useState<boolean>(false);
  const [editErrorMessage, setEditErrorMessage] = useState<string>("");
  const [newFile, setNewFile] = useState<File | null>(null);
  const [fileRemoved, setFileRemoved] = useState<boolean>(false);
  const { uploadFileToS3, progress, uploading, error } = useS3Upload();
  const [fileurl, setFileUrl] = useState<{ url: string; name: string }>({
    url: "",
    name: "",
  });
  const [files, setFiles] = useState<PolicyFile[]>([]);
  const [tagNames, setTagNames] = useState<{ [key: string]: string }>({});
  const tenantId = user?.tenantId ?? user?.tenant_id ?? "";
  const { canCreate, canRead, canUpdate, canDelete } = usePermissions();

  const fetchTagNames = useCallback(
    async (tagIds: string[], retryCount = 0) => {
      if (tagIds.length === 0) return;

      try {
        const response = await getAllPolicyTags();

        if (response.data.success === true) {
          const tagMap: { [key: string]: string } = {};
          const tags = response.data.results || response.data.message || [];

          if (Array.isArray(tags)) {
            tags.forEach((tag: any) => {
              const tagId = tag._id || tag.id;
              const tagName = tag.policytagName || tag.name || tag.tagName;
              if (tagId && tagName) {
                tagMap[tagId] = tagName;
              }
            });
          }

          setTagNames((prev) => ({ ...prev, ...tagMap }));
        } else if (retryCount < 1) {
          setTimeout(() => fetchTagNames(tagIds, retryCount + 1), 2000);
        }
      } catch (error) {
        if (retryCount < 1) {
          setTimeout(() => fetchTagNames(tagIds, retryCount + 1), 2000);
        }
      }
    },
    [],
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setEditFileModalOpen(false);
    setEditFile(null);
    setEditFileName("");
    setEditTags([]);
    setEditExpiry("");
    setEditReminder("");
    setEditRegion("");
    setEditPremises("");
    setEditAssignedTo("");
    setEditAvailableUsers([]);
    setEditCertificateName("");
    setEditIssueDate("");
    setEditIssuedBy("");
    setEditIsLifetime(false);
    setEditAvailableTags([]);
    setNewTag({ label: "", value: "" });
    setIsNewTag(false);
    setEditErrorMessage("");
    setNewFile(null);
    setFileRemoved(false);
  }, []);

  const fetchEditUsersByPremises = useCallback(async (premisesId: string) => {
    if (!premisesId || premisesId === "N/A" || premisesId === "ALL Premises") {
      setEditAvailableUsers([]);
      return;
    }
    try {
      setEditLoadingUsers(true);
      const response = await getUserDirectory();
      const users = response.data?.data || response.data?.message || [];
      const filteredUsers = users.filter((user: any) => {
        if (!user.premise) return false;
        if (Array.isArray(user.premise)) {
          return user.premise.some((p: any) => {
            if (typeof p === "string") return p === premisesId;
            if (p && typeof p === "object" && p.$oid)
              return p.$oid === premisesId;
            return p?._id === premisesId || p?.id === premisesId;
          });
        } else if (typeof user.premise === "string") {
          return user.premise === premisesId;
        } else if (user.premise && typeof user.premise === "object") {
          return (
            user.premise.$oid === premisesId ||
            user.premise._id === premisesId ||
            user.premise.id === premisesId
          );
        }
        return false;
      });
      const transformed: AssignedToUser[] = filteredUsers.map((user: any) => ({
        id: user._id || user.id,
        name:
          `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
          user.name ||
          "Unknown",
        email: user.email || "",
        employeeId: user.employeeId || "",
      }));
      setEditAvailableUsers(transformed);
    } catch (err) {
      setEditAvailableUsers([]);
    } finally {
      setEditLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    if (isCertificate) return;
    if (editFileModalOpen && editPremises) {
      fetchEditUsersByPremises(editPremises);
    } else if (!editPremises) {
      setEditAvailableUsers([]);
    }
  }, [
    isCertificate,
    editFileModalOpen,
    editPremises,
    fetchEditUsersByPremises,
  ]);

  useEffect(() => {
    if (isCertificate) return;
    if (editFileModalOpen) setEditAssignedTo("");
  }, [isCertificate, editPremises]);

  const fetchEditTags = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getAllPolicyTags();
      const fetchedTags = response.data.results || response.data.message || [];
      if (!Array.isArray(fetchedTags)) {
        console.error("Expected an array of tags, got:", fetchedTags);
        setEditErrorMessage("Invalid tag data from server");
        return;
      }
      setEditAvailableTags(
        fetchedTags.map((tag: any) => ({
          label: tag.policytagName || tag.policytagID,
          value: tag.id || tag.policytagID || tag.policytagName,
        })),
      );
    } catch (error: any) {
      console.error(
        "Error fetching tags:",
        error.message,
        error.response?.data,
      );
      setEditErrorMessage(
        "Failed to load tags: " +
          (error.response?.data?.error || error.message),
      );
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const handleAddTag = useCallback(async () => {
    if (!isDefined(newTag.label) || !isDefined(newTag.value)) {
      setEditErrorMessage("Tag cannot be empty");
      return;
    }

    setIsLoading(true);
    try {
      const { createPolicyTags } = usePolicyTagStore.getState();

      await createPolicyTags({
        policytagId: newTag.value,
        policytagName: newTag.value,
      });

      await fetchEditTags();

      const { policyTags } = usePolicyTagStore.getState();
      const newlyCreatedTag = policyTags.find(
        (tag) => tag.policytagName === newTag.value,
      );
      if (newlyCreatedTag) {
        setEditTags((prevSelected) => [
          ...prevSelected,
          newlyCreatedTag.id ||
            newlyCreatedTag.policytagId ||
            newlyCreatedTag.policytagName,
        ]);
      }

      setNewTag({ label: "", value: "" });
      setIsNewTag(false);
      setEditErrorMessage("");
    } catch (error: any) {
      console.error("Error adding tag:", error.message, error.response?.data);
      setEditErrorMessage(
        "Failed to add tag: " + (error.response?.data?.error || error.message),
      );
    } finally {
      setIsLoading(false);
    }
  }, [id, fetchEditTags]);

  const formatSize = (sizeInKB: number): string => {
    if (sizeInKB === 0) return "0 KB";
    if (sizeInKB < 1024) {
      return `${sizeInKB} KB`;
    }
    const sizeInMB = sizeInKB / 1024;
    if (sizeInMB < 1024) {
      return `${sizeInMB.toFixed(2)} MB`;
    }
    const sizeInGB = sizeInMB / 1024;
    return `${sizeInGB.toFixed(2)} GB`;
  };

  const filterFiles = useCallback(
    (files: PolicyFile[]) => {
      return files.filter((file) => {
        const regionMatch =
          selectedRegion && selectedRegion !== "ALL Regions"
            ? file.region &&
              file.region.trim().toLowerCase() ===
                selectedRegion.trim().toLowerCase()
            : true;
        const premisesMatch =
          selectedPremises && selectedPremises !== "ALL Premises"
            ? file.premises &&
              file.premises.trim().toLowerCase() ===
                selectedPremises.trim().toLowerCase()
            : true;
        return regionMatch && premisesMatch;
      });
    },
    [selectedRegion, selectedPremises],
  );

  // Shared per-file API-response mapping; certificate-only fields are only computed for that variant
  const mapApiFile = useCallback(
    (file: any): PolicyFile => {
      const base = {
        ...file,
        tags:
          typeof file.tags === "string"
            ? file.tags.split(",").filter((tag: string) => tag.trim() !== "")
            : file.tags || [],
        originalName: file.originalName || file.name,
      };
      if (!isCertificate) return base as PolicyFile;

      const hasLifetimeFlag = Object.prototype.hasOwnProperty.call(
        file,
        "isLifetime",
      );
      const isLifetime = hasLifetimeFlag
        ? normalizeIsLifetime(file.isLifetime)
        : isEmptyDateField(file.expiry) && isEmptyDateField(file.reminder);

      return {
        ...base,
        certificateName: file.certificateName || file.name,
        issueDate: file.issueDate || "",
        issuedBy: file.issuedBy || "",
        expiry: isLifetime ? "" : file.expiry || "",
        reminder: isLifetime ? "" : file.reminder || "",
        isLifetime,
      } as PolicyFile;
    },
    [isCertificate],
  );

  const fetchFiles = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: { region?: string; premises?: string } = {};
      if (selectedRegion && selectedRegion !== "ALL Regions")
        params.region = selectedRegion;
      if (selectedPremises && selectedPremises !== "ALL Premises")
        params.premises = selectedPremises;

      const res = await getFiles(id, params);
      if (res.data.resCode !== 200) {
        setResMessage(res.data.resMessage);
        setFiles([]);
      } else {
        const fetchedFiles: PolicyFile[] = (res.data.message.files || []).map(
          mapApiFile,
        );
        setFiles(fetchedFiles);

        const allTagIds = Array.from(
          new Set(fetchedFiles.flatMap((file) => file.tags)),
        );
        if (allTagIds.length > 0) {
          await fetchTagNames(allTagIds);
        }

        setResMessage(
          fetchedFiles.length === 0
            ? "No files found matching your criteria"
            : "",
        );
      }
    } catch (err: any) {
      setResMessage(err?.message ?? "Something went wrong");
      setFiles([]);
    } finally {
      setIsLoading(false);
    }
  }, [id, selectedRegion, selectedPremises, fetchTagNames, mapApiFile]);

  const handleUpload = useCallback(
    async (payload: PolicyUploadPayload) => {
      const { file, tags, expiryDate, reminder, region, premises } = payload;
      if (!file) {
        setResMessage("File cannot be empty");
        return;
      }
      setIsLoading(true);
      try {
        const uploadedFileUrl = await uploadFileToS3(file, {
          premiseId: premises,
        });
        // Create a more efficient payload - only send the new file
        const newFileData: Record<string, any> = {
          name: file.name,
          originalName: file.name,
          size: formatSize(file.size / 1024),
          tags,
          expiry: expiryDate,
          reminder,
          url: uploadedFileUrl,
          type: file.type,
          region,
          premises,
        };
        if (isCertificate) {
          newFileData.certificateName = payload.certificateName;
          newFileData.issueDate = payload.issueDate;
          newFileData.issuedBy = payload.issuedBy;
          newFileData.isLifetime = payload.isLifetime;
        } else if (payload.assignedTo) {
          newFileData.assignedTo = {
            name: payload.assignedTo.name,
            email: payload.assignedTo.email,
          };
        }

        const fileObj = {
          files: [newFileData],
          action: "add",
        };

        const fileUpRes = await updatefilefolder(fileObj, id);

        if (fileUpRes.data.resCode !== 200) {
          console.error("Upload failed:", fileUpRes.data.resMessage);
          setResMessage("");
        } else {
          const shouldCreateTask = isCertificate
            ? !payload.isLifetime && expiryDate && reminder
            : true;
          if (shouldCreateTask) {
            const dueDate = datesubstract(
              expiryDate,
              Number(reminder.split(" ")[0]) || 0,
            );
            const taskObject = {
              responsibility: "Building Occupier",
              task: file.name,
              premises,
              topic: "Fire System",
              reference: "Building Occupier Tasks",
              frequency: "One Time",
              fromDueDate: dueDate,
              toDueDate: expiryDate,
              furtherDetails: "File Upload",
              isRecurring: false,
              referenceId: uploadedFileUrl,
              folderId: id,
            };
            await addTask(taskObject);
          }

          await fetchFiles();
          setResMessage("");
        }
      } catch (err: any) {
        console.error(
          "Upload error:",
          err?.response?.data?.resMessage || err?.message,
        );
        setResMessage("");
      } finally {
        setIsLoading(false);
      }
    },
    [id, addTask, fetchFiles, isCertificate],
  );

  const searchFiles = useCallback(
    async (keyword: string) => {
      setIsLoading(true);
      try {
        const params: { [key: string]: any; keyword?: string } = {
          keyword: keyword.trim() || undefined,
          folderId: id,
        };
        if (selectedRegion && selectedRegion !== "ALL Regions")
          params.region = selectedRegion;
        if (selectedPremises && selectedPremises !== "ALL Premises")
          params.premises = selectedPremises;

        const res = await searchPolicies(params);
        if (res.data.resCode === 200) {
          const searchedFiles: PolicyFile[] = (res.data.message || []).map(
            mapApiFile,
          );
          setFiles(searchedFiles);

          const allTagIds = Array.from(
            new Set(searchedFiles.flatMap((file) => file.tags)),
          );
          if (allTagIds.length > 0) {
            await fetchTagNames(allTagIds);
          }

          setResMessage(
            searchedFiles.length === 0
              ? "No files found matching your criteria"
              : "",
          );
        } else {
          setFiles([]);
          setResMessage(res.data.resMessage || "No files found");
        }
      } catch (err: any) {
        setFiles([]);
        setResMessage(err?.message || "Something went wrong");
      } finally {
        setIsLoading(false);
      }
    },
    [id, selectedRegion, selectedPremises, fetchTagNames, mapApiFile],
  );

  const handleEditFile = useCallback(async () => {
    if (!editFile || !editFileName.trim()) {
      setResMessage("File name cannot be empty");
      return;
    }
    setEditFileModalOpen(false);
    setIsLoading(true);
    let updatedFileUrl = editFile.url;
    let updatedFileType = editFile.type;
    let updatedFileSize = editFile.size;
    let updatedOriginalName = editFile.originalName || editFile.name;

    if (newFile) {
      try {
        updatedFileUrl = await uploadFileToS3(newFile, {
          premiseId: editPremises,
        });
        updatedFileType = newFile.type;
        updatedFileSize = newFile.size + " KB";
        updatedOriginalName = newFile.name;
      } catch (err: any) {
        setResMessage(err?.message ?? "Failed to upload new file");
        setIsLoading(false);
        return;
      }
    } else if (fileRemoved) {
      setResMessage("No file selected after removal");
      setIsLoading(false);
      return;
    }

    let updatedFiles: PolicyFile[];
    if (isCertificate) {
      updatedFiles = files.map((file) =>
        file.url === editFile.url
          ? {
              ...file,
              name: editFileName,
              originalName: updatedOriginalName,
              tags: editTags,
              expiry: editIsLifetime ? "" : editExpiry,
              reminder: editIsLifetime ? "" : editReminder,
              region: editRegion,
              premises: editPremises,
              url: updatedFileUrl,
              type: updatedFileType,
              size: updatedFileSize,
              certificateName: editCertificateName,
              issueDate: editIssueDate,
              issuedBy: editIssuedBy,
              isLifetime: editIsLifetime,
            }
          : file,
      );
    } else {
      const selectedAssignee = editAssignedTo
        ? editAvailableUsers.find((u) => u.id === editAssignedTo)
        : null;
      const assignedToPayload = selectedAssignee
        ? { name: selectedAssignee.name, email: selectedAssignee.email }
        : undefined;

      updatedFiles = files.map((file) => {
        if (file.url !== editFile.url) return file;
        const updated: Record<string, any> = {
          ...file,
          name: editFileName,
          originalName: updatedOriginalName,
          tags: editTags,
          expiry: editExpiry,
          reminder: editReminder,
          region: editRegion,
          premises: editPremises,
          url: updatedFileUrl,
          type: updatedFileType,
          size: updatedFileSize,
        };
        updated.assignedTo = assignedToPayload ?? undefined;
        return updated;
      }) as PolicyFile[];
    }

    const fileObj = { files: updatedFiles };
    try {
      const res = await updatefilefolder(fileObj, id);
      if (res.data.resCode === 200) {
        setResMessage(res.data.resMessage || "File updated successfully");
        setFiles(filterFiles(updatedFiles));
      } else {
        console.error("File update failed:", res.data.resMessage);
        setResMessage("");
      }
    } catch (err: any) {
      console.error("File update error:", err.message);
      setResMessage("");
    } finally {
      setIsLoading(false);
      setEditFile(null);
      setEditFileName("");
      setEditTags([]);
      setEditExpiry("");
      setEditReminder("");
      setEditRegion("");
      setEditPremises("");
      setEditAssignedTo("");
      setEditAvailableUsers([]);
      setEditCertificateName("");
      setEditIssueDate("");
      setEditIssuedBy("");
      setEditIsLifetime(false);
      setEditAvailableTags([]);
      setNewTag({ label: "", value: "" });
      setIsNewTag(false);
      setEditErrorMessage("");
      setNewFile(null);
      setFileRemoved(false);
    }
  }, [
    editFile,
    editFileName,
    editTags,
    editExpiry,
    editReminder,
    editRegion,
    editPremises,
    editAssignedTo,
    editAvailableUsers,
    editCertificateName,
    editIssueDate,
    editIssuedBy,
    editIsLifetime,
    newFile,
    fileRemoved,
    files,
    id,
    filterFiles,
    isCertificate,
  ]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const selectedFile = e.target.files[0];
        if (selectedFile.size > 5 * 1024 * 1024) {
          setEditErrorMessage("File size cannot be more than 5MB");
          return;
        }
        setNewFile(selectedFile);
        setEditFileName(selectedFile.name);
        setEditErrorMessage("");
      }
    },
    [],
  );

  const handleRemoveFile = useCallback(() => {
    setFileRemoved(true);
    setNewFile(null);
  }, []);

  const openEditFileModal = useCallback(
    (file: PolicyFile) => {
      setEditFile(file);
      setEditFileName(file.name);
      setEditTags(file.tags);
      if (isCertificate) {
        const lifetime = normalizeIsLifetime(file.isLifetime);
        setEditIsLifetime(lifetime);
        setEditExpiry(lifetime ? "" : file.expiry || "");
        setEditReminder(lifetime ? "" : file.reminder || "");
        setEditCertificateName(file.certificateName || "");
        setEditIssueDate(file.issueDate || "");
        setEditIssuedBy(file.issuedBy || "");
      } else {
        setEditExpiry(formatDateForInput(file.expiry));
        setEditReminder(file.reminder || "");
        setEditAssignedTo("");
        setEditAvailableUsers([]);
      }
      setEditRegion(file.region || "");
      setEditPremises(file.premises || "");
      setEditFileModalOpen(true);
      fetchEditTags();
      setNewFile(null);
      setFileRemoved(false);
    },
    [fetchEditTags, isCertificate],
  );

  useEffect(() => {
    if (isCertificate) return;
    if (
      editFile &&
      editFile.assignedTo?.email &&
      editAvailableUsers.length > 0 &&
      !editAssignedTo
    ) {
      const match = editAvailableUsers.find(
        (u) => u.email === editFile.assignedTo?.email,
      );
      if (match) setEditAssignedTo(match.id);
    }
  }, [isCertificate, editFile, editAvailableUsers, editAssignedTo]);

  const getFileKey = (file: PolicyFile) => {
    // New format: DB stores object key
    if (file.url && !/^https?:\/\//.test(file.url)) {
      return file.url;
    }
    // Bad format: DB stores full URL or doesn't have url
    return file.originalName || file.name;
  };

  const handleDownloadFile = useCallback(async (file: PolicyFile) => {
    try {
      setIsLoading(true);
      const fileName = getFileKey(file);
      const signedUrl = await getS3SignedUrl(fileName, file.type);
      // Fetch the file content as a Blob
      const response = await fetch(signedUrl);
      if (!response.ok) {
        throw new Error("Failed to fetch file from S3");
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      setResMessage(err?.message ?? "Failed to download file");
    } finally {
      setIsLoading(false);
    }
  }, []);
  const handleDeleteFile = useCallback((file: PolicyFile) => {
    setFileToDelete(file);
  }, []);

  const confirmDeleteFile = useCallback(async () => {
    const file = fileToDelete;
    if (!file) return;
    setFileToDelete(null);
    setIsLoading(true);
    try {
      const remaining = files.filter((f) => f.url !== file.url);
      const res = await updatefilefolder({ files: remaining }, id);
      if (res.data.resCode === 200) {
        setResMessage("File deleted successfully");
        await fetchFiles();
      } else {
        setResMessage(res.data.resMessage || "Failed to delete file");
      }
    } catch (err: any) {
      setResMessage(err?.message ?? "Failed to delete file");
    } finally {
      setIsLoading(false);
    }
  }, [fileToDelete, files, id, fetchFiles]);

  useEffect(() => {
    fetchFiles();
  }, [id, selectedRegion, selectedPremises, fetchFiles]);

  useEffect(() => {
    if (searchQuery.trim()) {
      searchFiles(searchQuery);
    } else {
      fetchFiles();
    }
  }, [searchQuery, searchFiles, fetchFiles]);

  useEffect(() => {
    if (error !== null) setResMessage(error);
  }, [error]);

  const viewFile = useCallback(async (file: PolicyFile) => {
    try {
      // Use originalName if available, otherwise fall back to name
      // const fileName = file.originalName || file.name;
      // const fileurl = await getsignedurl(fileName, file.type);
      const fileName = getFileKey(file);
      const fileurl = await getS3SignedUrl(fileName, file.type);
      const filobj = {
        url: fileurl,
        name: file.name,
      };
      setFileUrl(filobj);
    } catch (err: any) {
      setResMessage("File not found");
    }
  }, []);

  return (
    <div className="px-2 py-4 mt-20">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">
          {isCertificate ? "Certificates / References" : ""}
        </h2>
        <div className="flex gap-2">
          <Dropdown
            selectedValue={selectedRegion}
            onChange={setSelectedRegion}
            type="region"
          />
          <Dropdown
            selectedValue={selectedPremises}
            onChange={setSelectedPremises}
            type="premises"
            selectedRegion={selectedRegion}
          />
          <div className="relative">
            <input
              type="text"
              placeholder="Search files"
              className="pl-10 pr-4 py-2 border rounded-md"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <img
              src={images.searchIcon}
              alt="Search"
              className="absolute left-3 top-2.5 w-5 h-5"
            />
          </div>
          {canCreate(permissionModule) && (
            <button
              className="flex items-center bg-teal-500 text-white px-4 py-2 rounded-full"
              onClick={() => setIsModalOpen(true)}
            >
              <img
                src={AddFilesIcon}
                alt="Add Files"
                className="w-5 h-5 mr-2"
              />
              {labels.addFileButtonLabel}
            </button>
          )}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-white bg-gray-700 px-4 py-2 rounded-full"
          >
            BACK
          </button>
        </div>
      </div>
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center p-4">
            <SpinLoader />
          </div>
        ) : (
          <>
            {files.length > 0 ? (
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-100 text-gray-600 uppercase text-sm">
                  <tr>
                    {isCertificate ? (
                      <>
                        <th className="p-2">Certificate/Reference Name</th>
                        <th className="p-2">Issue Date</th>
                        <th className="p-2">Issued By</th>
                      </>
                    ) : (
                      <th className="p-2">Files</th>
                    )}
                    <th className="p-2">File Size</th>
                    <th className="p-2">Tags</th>
                    <th className="p-2">Expiry Date</th>
                    <th className="p-2">Reminder Info</th>
                    <th className="p-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((file, index) => (
                    <tr key={index} className="border-t">
                      {isCertificate ? (
                        <>
                          <td className="p-2">
                            <span
                              className="cursor-pointer text-black hover:text-[#0fa9a6] active:text-[#0fa9a6] transition-colors"
                              onClick={() => viewFile(file)}
                            >
                              {file.certificateName}
                            </span>
                          </td>
                          <td className="p-2">{file.issueDate}</td>
                          <td className="p-2">{file.issuedBy}</td>
                        </>
                      ) : (
                        <td className="p-2">
                          <span
                            className="cursor-pointer text-black hover:text-[#0fa9a6] active:text-[#0fa9a6] transition-colors"
                            onClick={() => viewFile(file)}
                          >
                            {file.name}
                          </span>
                        </td>
                      )}
                      <td className="p-2">{formatSize(parseInt(file.size))}</td>
                      <td className="p-2">
                        {file.tags.length > 0
                          ? file.tags
                              .map((tagId) => {
                                const tagName = tagNames[tagId];
                                if (tagName) {
                                  return tagName;
                                } else if (Object.keys(tagNames).length === 0) {
                                  // If tag names haven't been loaded yet, show loading
                                  return "Loading...";
                                } else {
                                  // If tag name is not available, show a truncated ID
                                  return tagId.length > 8
                                    ? `${tagId.substring(0, 8)}...`
                                    : tagId;
                                }
                              })
                              .join(", ")
                          : "No tags"}
                      </td>
                      <td className="p-2">
                        {isCertificate
                          ? file.isLifetime
                            ? "Lifetime"
                            : file.expiry || "N/A"
                          : file.expiry}
                      </td>
                      <td className="p-2">
                        {isCertificate
                          ? file.isLifetime
                            ? "N/A"
                            : file.reminder || "N/A"
                          : file.reminder}
                      </td>
                      <td className="p-2 flex justify-center gap-2">
                        {canRead(permissionModule) && (
                          <button className="text-blue-500">
                            <img
                              src={images.viewIcon}
                              alt="View"
                              className="w-5 h-5"
                              onClick={() => viewFile(file)}
                            />
                          </button>
                        )}
                        {canUpdate(permissionModule) && (
                          <button className="text-green-500">
                            <img
                              src={images.editIcon}
                              alt="Edit"
                              className="w-5 h-5"
                              onClick={() => openEditFileModal(file)}
                            />
                          </button>
                        )}
                        {canRead(permissionModule) && (
                          <button className="text-gray-500">
                            <img
                              src={images.downloadIcon}
                              alt="Download"
                              className="w-5 h-5"
                              /* spell-checker: disable */
                              onClick={() => handleDownloadFile(file)}
                              /* spell-checker: enable */
                            />
                          </button>
                        )}
                        {canDelete(permissionModule) && (
                          <button className="text-red-500">
                            <Trash2
                              className="w-5 h-5"
                              onClick={() => handleDeleteFile(file)}
                            />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="align-middle p-4">
                {searchQuery.trim() || selectedRegion || selectedPremises
                  ? "No files found matching your criteria"
                  : "This folder is empty"}
              </p>
            )}
            {isModalOpen && (
              <AddFileModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                onUpload={handleUpload}
                folderId={id ?? ""}
                config={config}
              />
            )}
            <Modal
              isOpen={editFileModalOpen}
              onClose={handleEditModalClose}
              closeButton={isCertificate ? undefined : false}
            >
              <div className="bg-white rounded-lg shadow-lg w-[800px]">
                <div className="p-6 overflow-hidden">
                  <div className="mb-4">
                    <h2 className="text-xl font-semibold">
                      {isCertificate
                        ? "Edit Certificate/Reference"
                        : "Edit File"}
                    </h2>
                    <hr className="my-2 border-gray-300" />
                  </div>

                  {isCertificate && (
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="w-full">
                        <label className="block text-gray-600 font-semibold">
                          Certificate/Reference Name
                        </label>
                        <input
                          type="text"
                          className="w-full border rounded-md p-2"
                          placeholder="Enter certificate name"
                          value={editCertificateName}
                          onChange={(e) =>
                            setEditCertificateName(sanitizeName(e.target.value))
                          }
                        />
                      </div>
                      <div className="w-full">
                        <label className="block text-gray-600 font-semibold">
                          Issue Date
                        </label>
                        <input
                          type="date"
                          className="w-full border rounded-md p-2"
                          value={editIssueDate}
                          onChange={(e) => setEditIssueDate(e.target.value)}
                        />
                      </div>
                      <div className="w-full">
                        <label className="block text-gray-600 font-semibold">
                          Issued By
                        </label>
                        <input
                          type="text"
                          className="w-full border rounded-md p-2"
                          placeholder="Enter issued by"
                          value={editIssuedBy}
                          onChange={(e) => setEditIssuedBy(e.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="w-full">
                      <label className="block text-gray-600 font-semibold">
                        Tags
                        <span
                          className="uppercase float-end cursor-pointer text-teal-500"
                          style={{ fontSize: "12px" }}
                          onClick={() => setIsNewTag(!isNewTag)}
                        >
                          <PlusIcon className="w-4 inline" /> Add Tags
                        </span>
                      </label>
                      {isNewTag && (
                        <div className="flex">
                          <input
                            type="text"
                            className={`w-full border rounded-md p-2 ${
                              isLoading ? "opacity-50" : ""
                            }`}
                            value={newTag.label}
                            onChange={(e) =>
                              setNewTag({
                                label: e.target.value,
                                value: e.target.value,
                              })
                            }
                            disabled={isLoading}
                          />
                          <div
                            className={`p-2 bg-teal-500 ${
                              isLoading ? "opacity-50" : ""
                            }`}
                            onClick={isLoading ? undefined : handleAddTag}
                          >
                            <ArrowRight className="text-white" />
                          </div>
                        </div>
                      )}
                      {!isNewTag && (
                        <MultiSelectDropdown
                          key={editAvailableTags.length}
                          options={editAvailableTags}
                          selectedValues={editTags}
                          onChange={setEditTags}
                          placeholder={
                            isLoading ? "Loading tags..." : "Select Tags"
                          }
                        />
                      )}
                    </div>
                    <div className="w-full">
                      <label className="block text-gray-600 font-semibold">
                        Region
                      </label>
                      <Dropdown
                        selectedValue={editRegion}
                        onChange={setEditRegion}
                        type="region"
                      />
                    </div>
                    <div className="w-full">
                      <label className="block text-gray-600 font-semibold">
                        Premises
                      </label>
                      <Dropdown
                        selectedValue={editPremises}
                        onChange={setEditPremises}
                        type="premises"
                        selectedRegion={editRegion}
                      />
                    </div>
                  </div>

                  {isCertificate && (
                    <div className="mb-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="mr-2"
                          checked={editIsLifetime}
                          onChange={(e) => {
                            setEditIsLifetime(e.target.checked);
                            if (e.target.checked) {
                              setEditExpiry("");
                              setEditReminder("");
                            }
                          }}
                        />
                        <span className="text-gray-600 font-semibold">
                          Certificate is lifetime (no expiry date)
                        </span>
                      </label>
                      {editIsLifetime && (
                        <p className="text-sm text-gray-500 ml-6">
                          Expiry date and reminders are not required for
                          lifetime certificates.
                        </p>
                      )}
                    </div>
                  )}

                  {(!isCertificate || !editIsLifetime) && (
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="w-full">
                        <label className="block text-gray-600 font-semibold">
                          Expiry Date
                        </label>
                        <input
                          type="date"
                          className="w-full border rounded-md p-2"
                          value={editExpiry}
                          onChange={(e) => setEditExpiry(e.target.value)}
                          aria-label="Expiry date"
                        />
                      </div>
                      <div className="w-full">
                        <label className="block text-gray-600 font-semibold">
                          Reminder Days Before
                        </label>
                        <select
                          className="w-full border rounded-md p-2"
                          value={editReminder}
                          onChange={(e) => setEditReminder(e.target.value)}
                          aria-label="Reminder days before expiry"
                        >
                          <option value="">Select</option>
                          <option value="1 Week">1 Week</option>
                          <option value="2 Weeks">2 Weeks</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {!isCertificate && (
                    <div className="mb-4">
                      <label className="block text-gray-600 font-semibold">
                        Assigned To
                      </label>
                      <select
                        className="w-full border rounded-md p-2"
                        value={editAssignedTo}
                        onChange={(e) => setEditAssignedTo(e.target.value)}
                        disabled={editLoadingUsers}
                        aria-label="Assign file to user"
                      >
                        <option value="">
                          {editPremises
                            ? "Select assignee"
                            : "Select premises first"}
                        </option>
                        {editLoadingUsers ? (
                          <option disabled>Loading users...</option>
                        ) : editAvailableUsers.length > 0 ? (
                          editAvailableUsers.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name} ({u.email})
                            </option>
                          ))
                        ) : editPremises ? (
                          <option disabled>
                            No users for selected premises
                          </option>
                        ) : null}
                      </select>
                      {editAssignedTo && editAvailableUsers.length > 0 && (
                        <p className="text-sm text-blue-600 mt-1">
                          {
                            editAvailableUsers.find(
                              (u) => u.id === editAssignedTo,
                            )?.name
                          }
                        </p>
                      )}
                    </div>
                  )}

                  <div className="mb-4">
                    <label className="block text-gray-600 font-semibold">
                      File Name
                    </label>
                    <input
                      type="text"
                      className="w-full border rounded-md p-2"
                      placeholder="Enter File Name"
                      value={editFileName}
                      onChange={(e) =>
                        setEditFileName(sanitizeName(e.target.value))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-gray-600 font-semibold">
                      Upload File
                    </label>
                    <div className="mb-4">
                      {editFile && !fileRemoved && !newFile && (
                        <div className="flex items-center border p-2 rounded mb-2">
                          <span className="text-gray-600 flex-1">
                            {editFile.name}
                          </span>
                          <button
                            className="text-red-500"
                            onClick={handleRemoveFile}
                            title="Remove file"
                            aria-label="Remove file"
                          >
                            <XIcon className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                      {(fileRemoved || newFile || !editFile) && (
                        <div className="border-dashed border-2 p-6 text-center cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            id="editFileInput"
                            onChange={handleFileChange}
                          />
                          <label
                            htmlFor="editFileInput"
                            className="cursor-pointer"
                          >
                            {newFile ? (
                              <p className="text-gray-600">{newFile.name}</p>
                            ) : (
                              "Drag & Drop Your File Here Or Click"
                            )}
                          </label>
                          <p className="text-sm text-gray-500 mt-2">
                            Supported formats: DOCX, PDF, TXT, JPG, PNG (Max:
                            5MB)
                          </p>
                        </div>
                      )}
                    </div>
                    {isDefined(editErrorMessage) && (
                      <p className="text-red-600 text-sm">{editErrorMessage}</p>
                    )}
                  </div>
                </div>
                <div className="flex bg-[#61748E] px-3 py-2 justify-end gap-4">
                  <button
                    onClick={handleEditModalClose}
                    className="px-4 py-2 bg-gray-400 text-white rounded-full"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEditFile}
                    className="px-4 py-2 bg-teal-500 text-white rounded-full"
                    disabled={isLoading}
                  >
                    Update
                  </button>
                </div>
              </div>
            </Modal>
            {isLoading && (
              <div
                className="absmiddle bg-[#ffffff40]"
                style={{ width: "100vw", height: "100vh", zIndex: 9999 }}
              >
                <div
                  className="absmiddle"
                  style={{ width: "100px", height: "50px" }}
                >
                  <DotLoader />
                </div>
              </div>
            )}
            {uploading && (
              <div
                className="absmiddle"
                style={{ width: "100px", height: "50px" }}
              >
                Uploading ....
              </div>
            )}
            {progress > 0 && progress < 100 && (
              <div
                className="absmiddle"
                style={{ width: "100px", height: "50px" }}
              >
                <div
                  className="relative size-40"
                  style={{ transform: "scale(0.7)" }}
                >
                  <svg
                    className="size-full -rotate-90"
                    viewBox="0 0 36 36"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      strokeDasharray: 100,
                      strokeDashoffset: 100 - progress,
                    }}
                  >
                    <circle
                      cx="18"
                      cy="18"
                      r="16"
                      fill="none"
                      className="stroke-current text-gray-200 dark:text-neutral-700"
                      strokeWidth="2"
                    ></circle>
                    <circle
                      cx="18"
                      cy="18"
                      r="16"
                      fill="none"
                      className="stroke-current text-blue-600 dark:text-blue-500"
                      strokeWidth="2"
                      strokeLinecap="round"
                    ></circle>
                  </svg>
                  <div className="absolute top-1/2 start-1/2 transform -translate-y-1/2 -translate-x-1/2">
                    <span className="text-center text-2xl font-bold text-blue-600 dark:text-blue-500">
                      {progress}
                    </span>
                  </div>
                </div>
              </div>
            )}
            {fileurl.url !== "" && (
              <div
                className="absmiddle"
                style={{
                  width: "100vw",
                  height: "100vh",
                  zIndex: 999999,
                  backgroundColor: "rgba(0,0,0,0.5)",
                }}
              >
                <XIcon
                  className="absolute left-auto top-2 z-10 cursor-pointer bg-red-500 text-white"
                  style={{ right: "115px" }}
                  onClick={() => setFileUrl({ url: "", name: "" })}
                />
                <div
                  className="absmiddle bg-white border py-9"
                  style={{
                    width: "calc(100vw - 200px)",
                    height: "100vh",
                    overflow: "auto",
                    boxSizing: "border-box",
                  }}
                >
                  <DocumentViewer file={fileurl.url} name={fileurl.name} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <ConfirmModal
        isOpen={!!fileToDelete}
        title={`Are you sure you want to delete "${
          fileToDelete
            ? isCertificate
              ? fileToDelete.certificateName
              : fileToDelete.name
            : ""
        }"?`}
        message="Any related task will also be deleted."
        onConfirm={confirmDeleteFile}
        onCancel={() => setFileToDelete(null)}
      />
    </div>
  );
};

export default FolderView;
